import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { TEMPLATE_CONTENT } from './templateContent';

// ─── Helpers ─────────────────────────────────────────────────────────────

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10000);
};

/**
 * Core HTML → PDF renderer using html2canvas + jsPDF.
 *
 * IMPORTANT: html2canvas only captures elements that are actually rendered
 * in the visible viewport. Off-screen elements (negative top/left) are
 * skipped by the browser's layout engine and produce blank/corrupt output.
 *
 * Strategy: mount the invoice HTML inside a full-screen white overlay
 * that sits on top of everything (z-index 999999). The overlay acts as a
 * rendering canvas — the content is fully rendered, html2canvas captures
 * it perfectly, then the overlay is removed.
 */
const htmlToPdf = async (html: string, filename: string): Promise<void> => {
  const enhancedHtml = html.replace(
    '</head>',
    `<style>
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
      html, body { margin: 0 !important; }
    </style></head>`
  );

  // ── Full-screen white overlay so the invoice renders on-screen ──────────
  const overlay = document.createElement('div');
  overlay.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:999999',
    'background:#ffffff',
    'overflow:auto',
    'display:flex',
    'justify-content:center',
    'align-items:flex-start',
    'pointer-events:none',
  ].join(';');

  // Inner 794px container — exact A4 pixel width at 96 dpi
  const container = document.createElement('div');
  container.style.cssText = [
    'width:794px',
    'flex-shrink:0',
    'background:#ffffff',
  ].join(';');
  container.innerHTML = enhancedHtml;

  overlay.appendChild(container);
  document.body.appendChild(overlay);

  // Give the browser time to fully lay out and paint
  await new Promise((r) => setTimeout(r, 600));

  try {
    const contentHeight = Math.max(container.scrollHeight, 200);

    const canvas = await html2canvas(container, {
      scale: 2,           // 2× = crisp retina-quality output
      useCORS: true,
      backgroundColor: '#ffffff',
      width: 794,
      height: contentHeight,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      logging: false,
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();   // 210 mm
    const pageH = pdf.internal.pageSize.getHeight();  // 297 mm

    const imgData = canvas.toDataURL('image/jpeg', 0.97);
    // Height in mm maintaining exact canvas aspect ratio
    const imgH = (canvas.height / canvas.width) * pageW;

    if (imgH <= pageH) {
      // Single page — flush to top-left, full width
      pdf.addImage(imgData, 'JPEG', 0, 0, pageW, imgH);
    } else {
      // Multi-page: slice the image across pages
      let page = 0;
      while (page * pageH < imgH) {
        if (page > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -(page * pageH), pageW, imgH);
        page++;
      }
    }

    pdf.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
  } finally {
    document.body.removeChild(overlay);
  }
};

// ─── Public download functions ────────────────────────────────────────────

/** Download a template by ID as a PDF */
export const downloadAsPdf = async (templateId: string, filename: string): Promise<void> => {
  const content = TEMPLATE_CONTENT[templateId];
  if (!content) throw new Error('Template not found');
  await htmlToPdf(content.html, filename);
};

/** Download pre-filled HTML as a PDF */
export const downloadFilledAsPdf = async (html: string, filename: string): Promise<void> => {
  await htmlToPdf(html, filename);
};

/** Download pre-filled HTML as an .html file */
export const downloadFilledTemplate = (html: string, templateTitle: string): void => {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const safeName = templateTitle.replace(/[^a-z0-9]/gi, '_');
  triggerDownload(blob, `${safeName}.html`);
};

/** Download template by ID as an HTML file */
export const downloadTemplate = async (templateId: string, templateTitle: string): Promise<void> => {
  const content = TEMPLATE_CONTENT[templateId];
  if (!content) throw new Error('Template not found');
  const blob = new Blob([content.html], { type: 'text/html;charset=utf-8' });
  const safeName = templateTitle.replace(/[^a-z0-9]/gi, '_');
  triggerDownload(blob, `${safeName}.html`);
};

/** Download pre-filled HTML as a .doc file (Word-compatible) */
export const downloadFilledAsDoc = (html: string, filename: string): void => {
  const body = html
    .replace(/<!DOCTYPE html>[\s\S]*?<body[^>]*>/i, '')
    .replace(/<\/body>[\s\S]*?<\/html>/i, '');

  const wordHtml = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:w="urn:schemas-microsoft-com:office:word"
          xmlns="http://www.w3.org/TR/REC-html40">
    <head>
      <meta charset="utf-8">
      <meta name=ProgId content=Word.Document>
      <meta name=Generator content="Microsoft Word 15">
      <style>
        body { font-family: Calibri, Arial, sans-serif; margin: 1in; }
        @page { margin: 1in; }
      </style>
    </head>
    <body>${body}</body>
    </html>`;

  const blob = new Blob([wordHtml], { type: 'application/msword' });
  triggerDownload(blob, filename.endsWith('.doc') ? filename : `${filename}.doc`);
};

/** Download template by ID as a Word .doc */
export const downloadAsDoc = async (templateId: string, filename: string): Promise<void> => {
  const content = TEMPLATE_CONTENT[templateId];
  if (!content) throw new Error('Template not found');
  downloadFilledAsDoc(content.html, filename);
};

export const getDownloadFilename = (title: string, ext: string): string =>
  `${title.replace(/[^a-z0-9]/gi, '_')}.${ext}`;
